import { IconButton, TableInner, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { CountryName } from '../../../components/coutryName/CountryName';
import { TGiftPackType } from '../../../entities/giftPackages/queries';
import { pickProductsMapDefaultValues } from '../../../entities/products/shared';
import { TShipmentType } from '../../../entities/shipments/queries';
import { useShipmentAddressInfo } from '../../../entities/shipments/shared';
import { TProduct, TProductFieldsFragment } from '../../../generated/graphql';
import { getDefaultCustoms } from '../PrintLabelModal/customs';
import { PrintLabelModal } from '../PrintLabelModal/PrintLabelModal';
import styles from './PrintMultipleLabelModal.module.scss';

type TShipmentCustomsTableProps = {
  shipment: TShipmentType;
  setShipmentsToUpdate: React.Dispatch<React.SetStateAction<TShipmentType[]>>;
  defaultPackages: TGiftPackType[];
};

export const ShipmentCustomsTable = ({ shipment, setShipmentsToUpdate, defaultPackages }: TShipmentCustomsTableProps) => {
  const { recipientName } = useShipmentAddressInfo(shipment);

  const isInternational = useMemo(() => shipment.address?.country !== 'CAN', [shipment]);
  const [openModal, setOpenModal] = useState(false);

  const defaultCustoms = useMemo(() => {
    if (!isInternational) return null;
    if (!shipment.customsData) return getDefaultCustoms({ shipment, defaultPackages });
    return pickProductsMapDefaultValues(shipment.customsData as TProduct[]);
  }, [shipment, isInternational, defaultPackages]);

  useEffect(() => {
    if (!defaultCustoms) return;
    if (shipment.customsData?.length) return;
    setShipmentsToUpdate((prev) => prev.map((s) => (s.id === shipment.id ? { ...s, customsData: defaultCustoms } : s)));
  }, [shipment.id, shipment.customsData?.length, setShipmentsToUpdate, defaultCustoms]);

  return (
    <div className={styles.shipmentTableContainer}>
      <div className={styles.topTitle}>
        <h4 className={styles.topTitle}>
          Shipment for {recipientName} at {shipment.company?.name ?? '-'}
        </h4>
        <Tooltip message={isInternational ? 'Edit customs information' : 'Shipment destination is Canada'}>
          <IconButton disabled={!isInternational} icon={'edit'} onClick={() => setOpenModal(true)} variant='outlined' />
        </Tooltip>
      </div>

      {isInternational && (
        <div style={{ overflow: 'auto' }}>
          <TableInner<TProductFieldsFragment>
            enableSorting={true}
            defaultSorting={{ id: 'description', desc: false }}
            className={clsx(styles.containerTablePrintingMultipleLabel)}
            columns={[
              {
                header: 'HS Code',
                accessorKey: 'hsCode',
                cell: ({ cell }) => {
                  return (
                    <div className={clsx(styles.tableSpace75px)}>
                      <span>{cell.row.original.hsCode}</span>
                    </div>
                  );
                }
              },
              {
                header: 'Description',
                accessorKey: 'description',
                cell: ({ cell }) => {
                  return (
                    <div className={clsx(styles.tableSpace125px)}>
                      <span>{cell.row.original.description}</span>
                    </div>
                  );
                }
              },
              {
                header: 'Value',
                accessorKey: 'value',
                cell: ({ cell }) => {
                  return (
                    <div className={clsx(styles.tableSpace90px)}>
                      <span>${cell.row.original.value} CAD</span>
                    </div>
                  );
                }
              },
              {
                header: 'Quantity',
                accessorKey: 'quantity',
                cell: ({ cell }) => {
                  return (
                    <div className={clsx(styles.tableSpace75px)}>
                      <span>{cell.row.original.quantity}</span>
                    </div>
                  );
                }
              },
              {
                header: 'Merchant Product ID (SKU)',
                accessorKey: 'sku'
              },
              {
                header: 'Manufacturer Product ID',
                accessorKey: 'manufacturerNsProductId'
              },
              {
                header: 'Standardized Product ID Type',
                accessorKey: 'manufacturerProductIdType',
                cell: ({ row }) => {
                  const type = row.original.manufacturerProductIdType;
                  return type === 'undefined' ? 'Other / unspecified' : type?.toUpperCase();
                }
              },
              {
                header: 'Standardized Product ID',
                accessorKey: 'manufacturerProductId'
              },
              {
                header: 'Country',
                accessorKey: 'country',
                cell: ({ cell }) => {
                  return (
                    <div className={clsx(styles.tableSpace125px)}>
                      <CountryName name={cell.row.original.country} />
                    </div>
                  );
                }
              }
            ]}
            data={(shipment.customsData as TProductFieldsFragment[]) ?? []}
            emptyText='No products were found.'
            hoverableRows={true}
            virtual={false}
          />
        </div>
      )}
      {openModal && <PrintLabelModal shipment={shipment} onCancel={() => setOpenModal(false)} onSuccess={() => setOpenModal(false)} defaultPackages={defaultPackages} fromMultiple={true} setShipmentsToUpdate={setShipmentsToUpdate} />}
    </div>
  );
};
